import { createSlice } from '@reduxjs/toolkit';

import {
  IBillingInfo,
  PaybBillingPlan,
  PaymentInfo,
  SubscriptionProduct,
  SubscriptionSiteId,
} from '@global/types/types';
import { OrderPrice, PeriodInfo } from '@pages/SubscriptionDetails/types';

import { getSubscriptions } from '@global/store/slices/subscription/extraReducers/getSubscriptions';
import { updateBillingInformation } from '@global/store/slices/subscription/extraReducers/updateBillingInformation';
import { updatePaymentMethod } from '@global/store/slices/subscription/extraReducers/updatePaymantMethod';
import { cancelSubscriptionContract } from './extraReducers/cancelSubscriptionContract';

export interface subscriptionsByUid {
  uid: string | null;
  orderStatus: string;
  orderId: string | null;
  product: SubscriptionProduct;
  lastBillBiId: string;
  orderPrice: OrderPrice;
  periodInfo: PeriodInfo;
  paymentUid: string;
  paymentInfo: PaymentInfo;
  billingInfo: IBillingInfo;
  isCancelled: boolean;
  billingPlan: PaybBillingPlan;
  subscriptionSiteId: SubscriptionSiteId;
  userEmail: string;
}

export interface SubscriptionSliceState {
  isLoading: boolean;
  updating: boolean;
  isButtonLoading: boolean;
  isClicked: boolean;

  error?: string | null;
  notification: string | null;

  subscriptionsByUid: { [key: string]: subscriptionsByUid };
}

const initialState: SubscriptionSliceState = {
  isLoading: false,
  updating: false,
  isButtonLoading: false,
  isClicked: false,
  error: null,
  notification: null,

  subscriptionsByUid: {},
};

export const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    setNotification: (state, { payload }) => {
      state.notification = payload;
    },
    setIsCancelled: (
      state,
      {
        payload,
      }: { payload: { subscriptionUid: string; isCancelled: boolean } }
    ) => {
      const { subscriptionUid, isCancelled } = payload;
      state.subscriptionsByUid[subscriptionUid].isCancelled = isCancelled;
    },
    setPaymentInfo: (
      state,
      {
        payload,
      }: {
        payload: {
          subscriptionUid: string;
          paymentInfo: {
            cardNumber: string;
            expirationDate: string;
            wPaymentType: string;
          };
        };
      }
    ) => {
      const { subscriptionUid, paymentInfo } = payload;
      if (paymentInfo)
        state.subscriptionsByUid[subscriptionUid].paymentInfo = {
          ...paymentInfo,
          nameOnCard:
            state.subscriptionsByUid[subscriptionUid].paymentInfo.nameOnCard,
        };
    },
    setBillingInfo: (
      state,
      {
        payload,
      }: { payload: { subscriptionUid: string; billingInfo: IBillingInfo } }
    ) => {
      const { subscriptionUid, billingInfo } = payload;
      state.subscriptionsByUid[subscriptionUid].billingInfo = billingInfo;
    },
    setError: (state, { payload }) => {
      state.error = payload;
    },
    setIsClicked: (state, { payload }) => {
      state.isClicked = payload;
    },
    setIsButtonloading: (state, { payload }) => {
      state.isButtonLoading = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getSubscriptions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSubscriptions.fulfilled, (state, { payload }) => {
        state.isLoading = false;

        if (!payload) return;
        state.subscriptionsByUid = payload;
      })
      .addCase(getSubscriptions.rejected, (state, { payload }) => {
        state.error = payload;
        state.isLoading = false;
      })

      .addCase(updateBillingInformation.pending, (state) => {
        state.error = null;
        state.updating = true;
        state.isButtonLoading = true;
        state.isClicked = true;
      })
      .addCase(updateBillingInformation.fulfilled, (state, { payload }) => {
        state.notification = payload;
        state.updating = false;
        state.isButtonLoading = false;
        state.isClicked = false;
      })
      .addCase(updateBillingInformation.rejected, (state, { payload }) => {
        state.error = payload;
        state.updating = false;
        state.isButtonLoading = false;
        state.isClicked = false;
      })

      .addCase(updatePaymentMethod.pending, (state) => {
        state.error = null;
        state.updating = true;
        state.isButtonLoading = true;
      })
      .addCase(updatePaymentMethod.fulfilled, (state, { payload }) => {
        state.updating = false;
        state.notification = payload;
        state.isButtonLoading = false;
      })
      .addCase(updatePaymentMethod.rejected, (state, { payload }) => {
        state.error = payload;
        state.updating = false;
        state.isButtonLoading = false;
      })

      .addCase(cancelSubscriptionContract.pending, (state) => {
        state.error = null;
        state.updating = true;
      })
      .addCase(cancelSubscriptionContract.fulfilled, (state, { payload }) => {
        state.updating = false;
        state.notification = payload;
      })
      .addCase(cancelSubscriptionContract.rejected, (state, { payload }) => {
        state.error = payload;
        state.updating = false;
      });
  },
});

export const {
  setNotification,
  setIsCancelled,
  setPaymentInfo,
  setBillingInfo,
  setIsClicked,
  setIsButtonloading,
  setError,
} = subscriptionSlice.actions;

export const subscriptionReducer = subscriptionSlice.reducer;
